import { EventBus, IReactiveModel, ViewModel } from '@descomplica/microfrontend-core';
import { ArrayNotEmpty, IsEmail, IsNotEmpty, IsPhoneNumber } from 'class-validator';
import { action, makeAutoObservable, observable } from 'mobx';
import { toast } from 'react-toastify';

import type {
  AccountDto,
  CreateUserDto,
  RoleDto,
  ScopeDto,
} from '@/shared/application/interfaces/pegasus-io.gateway';
import { IsFullname, IsStrongPassword, ObservableForm } from '@/shared/presentation/forms/BaseForm';

import { CreateUserCollaboration } from '../../application/collaborations/create-user.collaboration';
import { GetRolesCollaboration } from '../../application/collaborations/get-roles.collaboration';
import { GetScopesCollaboration } from '../../application/collaborations/get-scopes.collaboration';

class Form {
  @IsFullname({ message: 'Nome inválido' })
  name: string;

  @IsEmail({}, { message: 'E-mail inválido' })
  email: string;

  @IsPhoneNumber('BR', { message: 'Telefone inválido' })
  phone: string;

  @IsNotEmpty({ message: 'Campo obrigatório' })
  countryCode = '55';

  @IsStrongPassword()
  password: string;

  @IsNotEmpty({ message: 'Campo obrigatório' })
  role: RoleDto;

  @ArrayNotEmpty({ message: 'Campo obrigatório' })
  scopes: ScopeDto[] = [];
}

export class CreatedAccountEvent {
  constructor(public readonly user: AccountDto) {}
}

@ViewModel({ singleton: true })
export class CreateUserViewModel implements IReactiveModel {
  @observable form = new ObservableForm(Form);
  @observable roles: RoleDto[] = [];
  @observable scopes: ScopeDto[] = [];
  @observable isSubmitting = false;
  @observable isLoading = false;
  @observable hasError = false;
  @observable isCreateModalOpen = false;

  constructor(
    private eventBus: EventBus,
    private createUserCollaboration: CreateUserCollaboration,
    private getRolesCollaboration: GetRolesCollaboration,
    private getScopesCollaboration: GetScopesCollaboration,
  ) {
    makeAutoObservable(this);
  }

  @action
  async onInit() {
    try {
      this.isLoading = true;

      const [{ data: roles }, { data: scopes }] = await Promise.all([
        this.getRolesCollaboration.run(),
        this.getScopesCollaboration.run(),
      ]);

      this.roles = roles;
      this.scopes = scopes.filter(
        (scope) => scope.guid !== process.env.REACT_APP_RECOGNITION_DESCOMPLICA_IO_SCOPE,
      );
    } catch (error) {
      console.error(error);
      this.hasError = true;
    } finally {
      this.isLoading = false;
    }
  }

  @action
  async create() {
    try {
      this.isSubmitting = true;

      const dto: CreateUserDto = {
        name: this.form.values.name?.trim(),
        email: this.form.values.email?.trim(),
        phone: this.form.values.countryCode + this.form.values.phone.replace(/\D/g, ''),
        password: this.form.values.password,
        roleGuid: this.form.values.role.guid,
        scopeGuids: this.form.values.scopes.map((scope) => scope.guid),
      };

      const { data } = await this.createUserCollaboration.run(dto);

      this.eventBus.emit(new CreatedAccountEvent(data));

      this.form.reset();

      toast.success('Conta criada com sucesso');

      this.isCreateModalOpen = false;
    } catch (error) {
      console.error(error);
      toast.error('Erro ao criar conta');
    } finally {
      this.isSubmitting = false;
    }
  }

  @action
  openCreateModal() {
    this.isCreateModalOpen = true;
  }

  @action
  closeCreateModal() {
    this.isCreateModalOpen = false;
  }
}
